import { Car } from '../types/car'
import { Service } from '../types/service'
import { PRODUCT_CATEGORIES } from '../constants/productCategories'

// Używaj istniejącego enum RequestMethod zamiast importować nieistniejący moduł
enum RequestMethod {
  POST = 'POST',
  GET = 'GET',
  DELETE = 'DELETE',
  PUT = 'PUT'
}

// Użyj istniejącego interfejsu AxiosResponse zamiast importować z axios
interface AxiosResponse<T = any> {
  data: T
  status: number
  statusText: string
  headers: Record<string, string>
  config?: Record<string, any>
}

const backendUrl = process.env.REACT_APP_BACKEND_URL ?? ''

// Definicja typów dla uploadFile
type UploadType = 'blog' | 'project' | 'avatar' | 'designer' | 'messages' | 'plant' | 'nursery' | 'plant-set' | 'other'

// Dodaj interfejs dla parametrów projektantów
export interface GetDesignersParams {
  page?: number
  limit?: number
  type?: string
}

// Dodaj interface dla parametrów pobierania szkółek
export interface GetNurseriesParams {
  page?: number
  limit?: number
  query?: string
  tradeTypes?: string[]
}

// Zaktualizuj interfejs UpdateProjectRequest
interface UpdateProjectRequest {
  name?: string
  description?: string
  isShared?: boolean
  status?: string
  clientName?: string
  clientEmail?: string
  clientPhone?: string
  budget?: number | null
  companyName?: string
  companyNip?: string
  companyAddress?: string
}

interface ProjectComment {
  id: string
  content: string
  createdAt: string
  updatedAt: string
  authorId: string
  authorName: string
  isPrivate: boolean
}

export class RequestMaker {
  private jwtToken = window.localStorage.getItem('Kreatlo-jwtToken')

  private updateJwtToken (): void {
    this.jwtToken = window.localStorage.getItem('Kreatlo-jwtToken')
  }

  private async makeRequest (method: RequestMethod, endPoint: string, body: any, config: any): Promise<AxiosResponse> {
    const finalUrl = `${backendUrl}/${endPoint}`

    // Inicjalizacja nagłówków jako obiekt Record, aby TypeScript mógł właściwie typować
    const headers: Record<string, string> = {
      ...(config?.headers || {})
    }

    const requestOptions: RequestInit = {
      method,
      headers
    }

    // Ustaw Content-Type tylko gdy mamy body do wysłania
    if (body && method !== RequestMethod.GET) {
      headers['Content-Type'] = 'application/json'
      requestOptions.body = JSON.stringify(body)
    }

    const response = await fetch(finalUrl, requestOptions)

    // Sprawdź, czy odpowiedź zawiera dane JSON
    const contentType = response.headers.get('content-type')
    let data = {}

    if (contentType && contentType.includes('application/json')) {
      try {
        data = await response.json()
      } catch (error) {
        console.error('Błąd podczas parsowania JSON:', error)
      }
    } else {
      // Jeśli nie ma danych JSON, pobierz tekst
      const text = await response.text()
    }

    // Budujemy obiekt zgodny z interfejsem AxiosResponse
    const axiosLikeResponse: AxiosResponse = {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: this.extractHeaders(response.headers),
      config
    }

    if (!response.ok) {
      console.error('Błąd odpowiedzi:', axiosLikeResponse)
      if (response.status === 401) {
        // window.location.href = '/login'
      }
      throw axiosLikeResponse
    }

    return axiosLikeResponse
  }

  private extractHeaders (headers: Headers): Record<string, string> {
    const headersObj: Record<string, string> = {}
    headers.forEach((value, key) => {
      headersObj[key] = value
    })
    return headersObj
  }

  private async makeAuthenticatedRequest (
    method: RequestMethod,
    endPoint: string,
    body?: any,
    config?: Record<string, any>
  ): Promise<AxiosResponse> {
    this.updateJwtToken()

    return await this.makeRequest(method, endPoint, body, {
      headers: {
        Authorization: `Bearer ${this.jwtToken ?? ''}`
      },
      ...config
    })
  }

  private async makeUnauthenticatedRequest (method: RequestMethod, endPoint: string, body?: any, config?: Record<string, any>): Promise<AxiosResponse> {
    return await this.makeRequest(method, endPoint, body ?? {}, config)
  }

  public async getUserContext (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, 'user/current')
  }

  public async getToken ({ username, password, googleToken }: { username?: string, password?: string, googleToken?: string }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'auth/login', {
      username,
      password,
      googleToken
    })
  }

  public async createCar (contrator: Partial<Car>): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'cars', contrator)
  }

  public async updateCar (contrator: Partial<Car>): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, 'cars', contrator)
  }

  public async deleteCars (ids: number[]): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'cars/delete-many', {
      ids
    })
  }

  public async createService (contrat: Partial<Service>): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'services', contrat)
  }

  public async updateService (contrat: Partial<Service>): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, 'services', contrat)
  }

  public async deleteServices (ids: number[]): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'services/delete-many', {
      ids
    })
  }

  public async registerUser (user: {
    username: string
    password: string
    phoneNumber: string
    email: string
    captchaToken: string | null
  }): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.POST, 'auth/register', user)
  }

  public async activateAccount (email: string, code: string): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.POST, 'auth/activate-account', {
      email,
      code
    })
  }

  public async updateUserCompanyDetails (user: {
    name: string
    taxId: string
    street: string
    city: string
    zipCode: string
    accountNumber: string
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, 'user/company-details', user)
  }

  public async updateUserDetails (user: {
    username: string
    phoneNumber: string
    email: string
    street?: string
    city?: string
    zipCode?: string
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, 'user/details', user)
  }

  public async changePassword (
    previousPassword: string,
    password: string
  ): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'user/change-password', {
      previousPassword,
      password
    })
  }

  public async sendEmailCode (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'activations/send-email', {}, { showLoader: false })
  }

  public async activateEmail (code: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'activations/activate-email', {
      code
    }, { showLoader: false })
  }

  public async emailOrUsernameExists (emailOrUsername: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, 'user/email-or-username-exists/' + emailOrUsername, {}, {
      showLoader: false
    })
  }

  public async phoneNumberExists (phoneNumber: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, 'user/phone-number-exists/' + phoneNumber, {}, {
      showLoader: false
    })
  }

  public async emailExists (email: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, 'user/email-exists/' + email, {}, {
      showLoader: false
    })
  }

  public async sendForgotPasswordCode (emailOrUsername: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.POST, 'forgot-password/send-email-code', {
      emailOrUsername
    })
  }

  public async validateForgotPasswordCode (emailOrUsername: string, code: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.POST, 'forgot-password/validate-email-code', {
      emailOrUsername,
      code
    })
  }

  public async changeForgottenPassword (emailOrUsername: string, code: string, password: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.POST, 'forgot-password/change', {
      emailOrUsername,
      code,
      password
    })
  }

  // Pobieranie wszystkich projektantów
  public async getDesigners (params?: GetDesignersParams): Promise<AxiosResponse> {
    // Budujemy string zapytania
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.type) queryParams.append('type', params.type)

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''

    return await this.makeUnauthenticatedRequest(RequestMethod.GET, `designers${queryString}`)
  }

  // Pobieranie konkretnego projektanta po ID
  public async getDesigner (id: number | string): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, `designers/${id}`)
  }

  // Pobieranie profilu projektanta dla bieżącego użytkownika
  public async getCurrentUserDesigner (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, 'designers/by-user/me')
  }

  // Tworzenie nowego profilu projektanta
  public async createDesigner (designerData: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'designers', designerData)
  }

  // Aktualizacja istniejącego profilu projektanta
  public async updateDesigner (id: number | string, designerData: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `designers/${id}`, designerData)
  }

  // Usuwanie profilu projektanta
  public async deleteDesigner (id: number | string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `designers/${id}`)
  }

  // Metody dla wiadomości

  // Pobieranie listy konwersacji użytkownika
  public async getConversations (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, 'messages/conversations')
  }

  // Pobieranie lub tworzenie konwersacji z określonym użytkownikiem
  public async getOrCreateConversation (recipientId: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `messages/conversation/${recipientId}`)
  }

  // Pobieranie wiadomości dla danej konwersacji
  public async getConversationMessages (conversationId: number, page = 1, limit = 20): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.GET,
      `messages/conversation/${conversationId}/messages?page=${page}&limit=${limit}`
    )
  }

  // Wysyłanie nowej wiadomości
  public async sendMessage (messageData: {
    content: string
    recipientId: number
    conversationId?: number
    attachments?: Array<{
      name: string
      type: string
      size: number
      url: string
    }>
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'messages/send', messageData)
  }

  // Oznaczanie wiadomości w konwersacji jako przeczytane
  public async markConversationAsRead (conversationId: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.POST,
      `messages/mark-as-read/${conversationId}`,
      {}
    )
  }

  // Projekty

  public async getProjects (params?: {
    sortBy?: string
    sortOrder?: string
    status?: string
    searchQuery?: string
  }): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams()

    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.searchQuery) queryParams.append('searchQuery', params.searchQuery)
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''

    return await this.makeAuthenticatedRequest(RequestMethod.GET, `projects${queryString}`)
  }

  public async getProject (id: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `projects/${id}`)
  }

  public async createProject (data: { name: string, description?: string }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'projects', data)
  }

  public async updateProject (id: string, data: UpdateProjectRequest): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `projects/${id}`, data)
  }

  public async deleteProject (id: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `projects/${id}`)
  }

  public async shareProject (projectId: string, data?: { selectedColumns?: { [key: string]: boolean } }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${projectId}/share`, (data != null) || {})
  }

  public async unshareProject (projectId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${projectId}/unshare`, {})
  }

  public async sendProjectInvitations (id: string, data: { emails: string[], shareUrl: string }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${id}/send-invitations`, data)
  }

  public async addProjectStage (projectId: string, data: {
    name: string
    startDate?: string
    endDate?: string
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${projectId}/stages`, data)
  }

  public async updateProjectStage (projectId: string, stageId: string, data: {
    name: string
    startDate?: string
    endDate?: string
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `projects/${projectId}/stages/${stageId}`, data)
  }

  public async deleteProjectStage (projectId: string, stageId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `projects/${projectId}/stages/${stageId}`)
  }

  public async addProjectItem (projectId: string, data: {
    name?: string
    type: 'product' | 'service' | 'note' | 'visualization' | 'attachment'
    price: number
    quantity: number
    unit: string
    description?: string
    stageId: string
    url?: string
    dimensions?: string
    imageUrl?: string
    deliveryCost?: number
    discountPercentage?: number
    marginPercentage?: number
    vatPercentage?: number
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${projectId}/items`, data)
  }

  public async updateProjectItem (projectId: string, itemId: string, data: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `projects/${projectId}/items/${itemId}`, data)
  }

  public async deleteProjectItem (projectId: string, itemId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `projects/${projectId}/items/${itemId}`)
  }

  public async reorderProjectItems (projectId: string, updates: Array<{ id: string, position: number, stageId?: string }>): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.POST,
      `projects/${projectId}/reorder-items`,
      updates
    )
  }

  // Tablice zadań (Task Boards)

  public async getTaskBoards (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, 'task-boards')
  }

  public async getTaskBoard (id: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `task-boards/${id}`)
  }

  public async createTaskBoard (data: { title: string, description?: string }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'task-boards', data)
  }

  public async updateTaskBoard (id: string, data: { title?: string, description?: string }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `task-boards/${id}`, data)
  }

  public async deleteTaskBoard (id: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `task-boards/${id}`)
  }

  public async inviteToTaskBoard (id: string, email: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `task-boards/${id}/invite`, { email })
  }

  public async removeTaskBoardMember (boardId: string, memberId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `task-boards/${boardId}/members/${memberId}`)
  }

  public async createTaskColumn (boardId: string, title: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `task-boards/${boardId}/columns`, { title })
  }

  public async updateTaskColumn (boardId: string, columnId: string, title: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `task-boards/${boardId}/columns/${columnId}`, { title })
  }

  public async deleteTaskColumn (boardId: string, columnId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `task-boards/${boardId}/columns/${columnId}`)
  }

  public async createTaskCard (boardId: string, data: { title: string, description?: string, columnId: string, priority?: number }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `task-boards/${boardId}/cards`, data)
  }

  public async updateTaskCard (boardId: string, cardId: string, data: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `task-boards/${boardId}/cards/${cardId}`, data)
  }

  public async deleteTaskCard (boardId: string, cardId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `task-boards/${boardId}/cards/${cardId}`)
  }

  public async assignTaskCard (boardId: string, cardId: string, userId: string | null): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `task-boards/${boardId}/cards/${cardId}/assign`, { userId })
  }

  public async addTaskComment (boardId: string, cardId: string, data: { content: string }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `task-boards/${boardId}/cards/${cardId}/comments`, data)
  }

  public async reorderTaskCards (boardId: string, cardUpdates: Array<{ id: string, position: number, columnId?: string }>): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `task-boards/${boardId}/reorder-cards`, cardUpdates)
  }

  // Metody dla współpracowników projektu
  public async getProjectCollaborators (projectId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `projects/${projectId}/collaborators`)
  }

  public async inviteProjectCollaborator (projectId: string, data: { email: string, role: string, firstName?: string, lastName?: string }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${projectId}/collaborators`, data)
  }

  public async updateProjectCollaborator (projectId: string, collaboratorId: string, data: { role: string }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `projects/${projectId}/collaborators/${collaboratorId}`, data)
  }

  public async removeProjectCollaborator (projectId: string, collaboratorId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `projects/${projectId}/collaborators/${collaboratorId}`)
  }

  // Rośliny (Plants)
  public async getPlants (params?: {
    page?: number
    limit?: number
    category?: string[]
    flowerColor?: string[]
    soilType?: string[]
    sunExposure?: string[]
    form?: string[]
    habit?: string[]
    leafType?: string
    soilMoisture?: string[]
    soilPh?: string[]
    features?: string[]
    gardenTypes?: string[]
    usage?: string[]
    growthRate?: string[]
    leafColor?: string[]
    floweringSeason?: string[]
    fruits?: string
    fruitingSeason?: string[]
    hardiness?: string[]
    heightMin?: number
    heightMax?: number
    spreadMin?: number
    spreadMax?: number
    searchTerm?: string
  }): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if ((params?.category) != null) queryParams.append('category', params.category.join(','))
    if ((params?.growthRate) != null) queryParams.append('growthRate', params.growthRate.join(','))
    if (params?.fruits) queryParams.append('fruits', params.fruits)
    if (params?.leafType) queryParams.append('leafType', params.leafType)

    if (params?.heightMin) queryParams.append('heightMin', params.heightMin.toString())
    if (params?.heightMax) queryParams.append('heightMax', params.heightMax.toString())
    if (params?.spreadMin) queryParams.append('spreadMin', params.spreadMin.toString())
    if (params?.spreadMax) queryParams.append('spreadMax', params.spreadMax.toString())
    if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm)

    // Dodawanie parametrów tablicowych
    const arrayParams: Array<{ key: string, values?: string[] }> = [
      { key: 'flowerColor', values: params?.flowerColor },
      { key: 'soilType', values: params?.soilType },
      { key: 'sunExposure', values: params?.sunExposure },
      { key: 'form', values: params?.form },
      { key: 'habit', values: params?.habit },
      { key: 'soilMoisture', values: params?.soilMoisture },
      { key: 'soilPh', values: params?.soilPh },
      { key: 'features', values: params?.features },
      { key: 'gardenTypes', values: params?.gardenTypes },
      { key: 'usage', values: params?.usage },
      { key: 'leafColor', values: params?.leafColor },
      { key: 'floweringSeason', values: params?.floweringSeason },
      { key: 'fruitingSeason', values: params?.fruitingSeason },
      { key: 'hardiness', values: params?.hardiness }
    ]

    arrayParams.forEach(param => {
      if ((param.values != null) && param.values.length > 0) {
        param.values.forEach(value => queryParams.append(param.key, value))
      }
    })

    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `plants${query}`)
  }

  public async getPlantCategories (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, 'plants/categories')
  }

  public async getPlant (id: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `plants/${id}`)
  }

  public async createPlant (plantData: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'plants', plantData)
  }

  public async updatePlant (id: number, plantData: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `plants/${id}`, plantData)
  }

  public async deletePlant (id: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `plants/${id}`)
  }

  // Brands API

  // Metoda do pobierania marek z filtrami
  public async getBrands (params?: {
    page?: number
    limit?: number
    category?: string
    searchQuery?: string
    fileFormat?: string
    sort?: string
  }): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.category) queryParams.append('category', params.category)
    if (params?.searchQuery) queryParams.append('search', params.searchQuery)
    if (params?.fileFormat) queryParams.append('fileFormat', params.fileFormat)
    if (params?.sort) queryParams.append('sort', params.sort)
    const queryString = queryParams.toString()
    const url = queryString ? `brands?${queryString}` : 'brands'

    return await this.makeUnauthenticatedRequest(RequestMethod.GET, url)
  }

  // Metoda do pobierania pojedynczej marki
  public async getBrand (id: number): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, `brands/${id}`)
  }

  // Metoda do tworzenia marki
  public async createBrand (data: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'brands', data)
  }

  // Metoda do aktualizacji marki
  public async updateBrand (id: number, data: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `brands/${id}`, data)
  }

  // Metoda do usuwania marki
  public async deleteBrand (id: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `brands/${id}`)
  }

  // Metoda do pobierania kategorii produktów
  public async getProductCategories (): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, 'brands/product-categories')
  }

  // Metoda do pobierania formatów plików
  public async getBrandFileFormats (): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, 'brands/file-formats')
  }

  // Metoda do pobierania produktów z filtrami
  public async getProducts (params: {
    page?: number
    limit?: number
    brandId?: number
    category?: string
    searchQuery?: string
    fileFormat?: string
    sort?: string
  }): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams()

    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.brandId) queryParams.append('brandId', params.brandId.toString())
    if (params.category) queryParams.append('category', params.category)
    if (params.searchQuery) queryParams.append('search', params.searchQuery)
    if (params.fileFormat) queryParams.append('fileFormat', params.fileFormat)
    if (params.sort) queryParams.append('sort', params.sort)

    return await this.makeUnauthenticatedRequest(
      RequestMethod.GET,
      `brands/products?${queryParams.toString()}`
    )
  }

  // Metoda do pobierania pojedynczego produktu
  public async getProduct (id: number): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, `brands/products/${id}`)
  }

  // Metoda do tworzenia produktu
  public async createProduct (data: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'brands/products', data)
  }

  // Metoda do aktualizacji produktu
  public async updateProduct (id: number, data: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `brands/products/${id}`, data)
  }

  // Metoda do usuwania produktu
  public async deleteProduct (id: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `brands/products/${id}`)
  }

  // Blog Posts API

  // Pobieranie artykułów na blogu
  public async getBlogPosts (params?: {
    page?: number
    limit?: number
    searchQuery?: string
    categories?: string[]
  }): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.searchQuery) queryParams.append('searchQuery', params.searchQuery)

    if (((params?.categories) != null) && params.categories.length > 0) {
      params.categories.forEach(category => queryParams.append('categories', category))
    }

    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, `blog${query}`)
  }

  // Pobieranie artykułów na blogu dla panelu admina
  public async getBlogPostsAdmin (params?: {
    page?: number
    limit?: number
    searchQuery?: string
    categories?: string[]
  }): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.searchQuery) queryParams.append('searchQuery', params.searchQuery)

    if (((params?.categories) != null) && params.categories.length > 0) {
      params.categories.forEach(category => queryParams.append('categories', category))
    }

    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `blog/admin${query}`)
  }

  // Pobieranie pojedynczego artykułu po ID
  public async getBlogPostById (id: number): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, `blog/id/${id}`)
  }

  // Pobieranie pojedynczego artykułu po slug (URL)
  public async getBlogPostBySlug (slug: string): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, `blog/${slug}`)
  }

  // Tworzenie nowego artykułu
  public async createBlogPost (blogPostData: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'blog', blogPostData)
  }

  // Aktualizacja artykułu
  public async updateBlogPost (id: number, blogPostData: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `blog/${id}`, blogPostData)
  }

  // Usuwanie artykułu
  public async deleteBlogPost (id: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `blog/${id}`)
  }

  // Pobieranie kategorii na blogu
  public async getBlogCategories (): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, 'blog/categories')
  }

  // Pobieranie popularnych artykułów
  public async getPopularBlogPosts (limit?: number): Promise<AxiosResponse> {
    const query = limit ? `?limit=${limit}` : ''
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, `blog/popular${query}`)
  }

  // Metody do interakcji ze szkółkami
  public async getNurseries (params?: GetNurseriesParams): Promise<AxiosResponse> {
    let endpoint = 'nurseries'

    if (params != null) {
      const queryParams = new URLSearchParams()

      if (params.page) queryParams.append('page', params.page.toString())
      if (params.limit) queryParams.append('limit', params.limit.toString())
      if (params.query) queryParams.append('query', params.query)
      if ((params.tradeTypes != null) && params.tradeTypes.length > 0) {
        params.tradeTypes.forEach(type => queryParams.append('tradeType', type))
      }

      const queryString = queryParams.toString()
      if (queryString) {
        endpoint += `?${queryString}`
      }
    }

    return await this.makeAuthenticatedRequest(RequestMethod.GET, endpoint)
  }

  public async getNursery (id: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `nurseries/${id}`)
  }

  public async createNursery (data: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'nurseries', data)
  }

  public async updateNursery (id: number, data: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `nurseries/${id}`, data)
  }

  public async deleteNursery (id: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `nurseries/${id}`)
  }

  // Metody dla zestawów roślin
  public async getPlantSets (params?: {
    page?: number
    limit?: number
    type?: string
    tags?: string[]
  }): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.type) queryParams.append('type', params.type)

    if (((params?.tags) != null) && params.tags.length > 0) {
      params.tags.forEach(tag => queryParams.append('tags', tag))
    }

    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `plant-sets${query}`)
  }

  public async getPlantSetTypes (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, 'plant-sets/types')
  }

  public async getPlantSet (id: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `plant-sets/${id}`)
  }

  public async createPlantSet (plantSetData: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'plant-sets', plantSetData)
  }

  public async updatePlantSet (id: number, plantSetData: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `plant-sets/${id}`, plantSetData)
  }

  public async deletePlantSet (id: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `plant-sets/${id}`)
  }

  // Śledzenie kliknięć w dane kontaktowe projektanta (dostępne również dla niezalogowanych)
  public async trackDesignerContactClick (
    designerId: number | string,
    contactType: 'phone' | 'email' | 'website' | 'address'
  ): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(
      RequestMethod.POST,
      `designers/${designerId}/track-contact-click`,
      { contactType }
    )
  }

  // Pobieranie statystyk kliknięć dla projektanta (tylko dla właściciela)
  public async getDesignerClickStats (designerId: number | string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.GET,
      `designers/${designerId}/click-stats`
    )
  }

  // Śledzenie odwiedzin profilu projektanta
  public async trackDesignerProfileVisit (designerId: number | string): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(
      RequestMethod.POST,
      `designers/${designerId}/track-profile-visit`,
      {}
    )
  }

  // Pobieranie statystyk odwiedzin profilu projektanta (tylko dla właściciela)
  public async getDesignerVisitStats (designerId: number | string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.GET,
      `designers/${designerId}/visit-stats`
    )
  }

  // Metody dla komentarzy do projektów
  public async getProjectComments (projectId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `projects/${projectId}/comments`)
  }

  public async addProjectComment (projectId: string, data: { content: string, isPrivate: boolean }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${projectId}/comments`, data)
  }

  public async updateProjectComment (projectId: string, commentId: string, data: { content: string, isPrivate: boolean }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `projects/${projectId}/comments/${commentId}`, data)
  }

  public async deleteProjectComment (projectId: string, commentId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `projects/${projectId}/comments/${commentId}`)
  }

  // Metody dla komentarzy do elementów projektu
  public async getProjectItemComments (projectId: string, itemId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.GET,
      `projects/${projectId}/items/${itemId}/comments`
    )
  }

  public async addProjectItemComment (
    projectId: string,
    itemId: string,
    data: { content: string }
  ): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.POST,
      `projects/${projectId}/items/${itemId}/comments`,
      data
    )
  }

  public async addProjectItemCommentUnauthorized (
    projectId: string,
    itemId: string,
    data: { content: string, projectUuid: string }
  ): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(
      RequestMethod.POST,
      `projects/${projectId}/items/${itemId}/comments/unauthorized`,
      data
    )
  }

  public async updateProjectItemComment (
    projectId: string,
    itemId: string,
    commentId: string,
    data: { content: string }
  ): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.PUT,
      `projects/${projectId}/items/${itemId}/comments/${commentId}`,
      data
    )
  }

  public async deleteProjectItemComment (
    projectId: string,
    itemId: string,
    commentId: string
  ): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.DELETE,
      `projects/${projectId}/items/${itemId}/comments/${commentId}`
    )
  }

  private async makeFileUploadRequest (
    method: RequestMethod,
    endPoint: string,
    file: File,
    additionalData?: Record<string, any>
  ): Promise<AxiosResponse> {
    this.updateJwtToken()
    const finalUrl = `${backendUrl}/${endPoint}`

    const formData = new FormData()
    formData.append('file', file)

    // Dodaj dodatkowe dane, jeśli istnieją
    if (additionalData != null) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key])
      })
    }

    const requestOptions: RequestInit = {
      method,
      headers: {
        Authorization: `Bearer ${this.jwtToken ?? ''}`
      },
      body: formData
    }

    const response = await fetch(finalUrl, requestOptions)
    const data = await response.json().catch(() => ({}))

    const axiosLikeResponse: AxiosResponse = {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: this.extractHeaders(response.headers)
    }

    if (!response.ok) {
      throw axiosLikeResponse
    }

    return axiosLikeResponse
  }

  // Metoda do wysyłania plików
  public async uploadFile (file: File, type: UploadType): Promise<AxiosResponse> {
    this.updateJwtToken()

    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)

    const requestOptions: RequestInit = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.jwtToken}`
      },
      body: formData
    }

    const response = await fetch(`${backendUrl}/uploads`, requestOptions)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw JSON.stringify({
        response: {
          status: response.status,
          data: errorData
        }
      })
    }

    const data = await response.json()

    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: this.extractHeaders(response.headers),
      config: {}
    }
  }

  // Metoda do pobierania presigned URL dla istniejącego pliku
  public async getPresignedUrl (fileName: string, expirySeconds?: number): Promise<AxiosResponse> {
    let endpoint = `uploads/presigned-url/${encodeURIComponent(fileName)}`
    if (expirySeconds) {
      endpoint += `?expirySeconds=${expirySeconds}`
    }

    return await this.makeAuthenticatedRequest(RequestMethod.GET, endpoint)
  }

  // Metoda do migracji kluczy plików w profilach projektantów
  public async migrateDesignerImageKeys (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'designers/migrate-image-keys')
  }

  // Metoda do pobierania presigned URL dla projektanta
  public async getDesignerPresignedUrls (designerId: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `designers/${designerId}/presigned-urls`)
  }

  public async updateUserPackage (updateUserPackageDto: {
    package: string
    packageExpiredAt?: Date
    packageUsersNumber?: number
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, 'user/package', updateUserPackageDto)
  }

  // Metoda do pobierania presigned URL dla rośliny
  public async getPlantPresignedUrls (plantId: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.GET,
      `plants/${plantId}/presigned-urls`
    )
  }

  // Metoda do pobierania presigned URL dla szkółki
  public async getNurseryPresignedUrls (nurseryId: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `nurseries/${nurseryId}/presigned-urls`)
  }

  // Metoda do pobierania presigned URL dla zestawu roślin
  public async getPlantSetPresignedUrls (plantSetId: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `plant-sets/${plantSetId}/presigned-urls`)
  }

  public async createCard (boardId: string, data: {
    title: string
    description: string
    columnId: string
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `task-boards/${boardId}/cards`, data)
  }

  // Dodanie metod dla płatności
  public async createPayment (paymentData: {
    package: string
    amount: number
    description: string
    customerEmail?: string
    packageExpiredAt?: Date
    packageUsersNumber?: number
  }): Promise<AxiosResponse> {
    const userEmail = localStorage.getItem('Kreatlo-userEmail') || ''

    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'payments', {
      ...paymentData,
      customerEmail: paymentData.customerEmail || userEmail
    })
  }

  public async getUserPayments (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, 'payments/user')
  }

  public async getPaymentById (id: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `payments/${id}`)
  }

  public async getPaymentStatus (id: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `payments/status/${id}`)
  }

  public async searchUsers (query: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.GET,
      `user/search?query=${encodeURIComponent(query)}`
    )
  }

  // Nowa metoda do śledzenia odwiedzin rośliny
  public async trackPlantVisit (plantId: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.POST,
      `plants/${plantId}/track-visit`,
      {}
    )
  }

  // Nowa metoda do pobierania statystyk odwiedzin rośliny
  public async getPlantVisitStats (plantId: number): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.GET,
      `plants/${plantId}/visit-stats`
    )
  }

  async updateProjectItemStatus (projectId: string, itemId: string, status: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.PUT,
      `projects/${projectId}/items/${itemId}/status`,
      { status }
    )
  }

  async updateProjectItemInternalStatus (projectId: string, itemId: string, internalStatus: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.PUT,
      `projects/${projectId}/items/${itemId}/internal-status`,
      { internalStatus }
    )
  }

  // Dodaj nową metodę do pobierania udostępnionego projektu po ID linku udostępniania
  public async getSharedProject (shareId: string): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, `projects/shared/${shareId}`, {})
  }

  async copyProjectItem (projectId: string, itemId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${projectId}/items/${itemId}/copy`, {})
  }

  // Metoda do dodawania produktu
  public async addProduct (productData: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'products', productData)
  }

  // Metoda do przesyłania obrazka produktu
  public async uploadProductImage (file: File): Promise<AxiosResponse> {
    return await this.uploadFile(file, 'other')
  }

  // Metoda do pobierania zależności dla karty
  public async getTaskCardDependencies (boardId: string, cardId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.GET,
      `task-boards/${boardId}/cards/${cardId}/dependencies`
    )
  }

  // Metoda do dodawania zależności, aby obsługiwała wszystkie typy
  public async addTaskCardDependency (
    boardId: string,
    cardId: string,
    data: {
      dependencyType: string
      stageName: string
      customTaskName: string
    }
  ): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.POST,
      `task-boards/${boardId}/cards/${cardId}/dependencies`,
      data
    )
  }

  // Metoda do usuwania zależności
  public async removeTaskCardDependency (
    boardId: string,
    cardId: string,
    dependencyId: string
  ): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.DELETE,
      `task-boards/${boardId}/cards/${cardId}/dependencies/${dependencyId}`
    )
  }

  public async updateTaskCardDependency (
    boardId: string,
    cardId: string,
    dependencyId: string,
    data: {
      dependencyType: string
      stageName: string
      customTaskName: string
    }
  ): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.PUT,
      `task-boards/${boardId}/cards/${cardId}/dependencies/${dependencyId}`,
      data
    )
  }

  public async getProjectAttachments (projectId: string, folderId: string | null, isList: boolean): Promise<AxiosResponse> {
    let url = `projects/${projectId}/attachments?isList=${isList}`
    if (folderId !== undefined) {
      url += `&folderId=${folderId || 'null'}`
    }
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, url)
  }

  public async addProjectAttachment (projectId: string, data: {
    name: string
    type: string
    size: number
    url: string
    fileKey: string
    isList: boolean
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${projectId}/attachments`, data)
  }

  public async deleteProjectAttachment (projectId: string, attachmentId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `projects/${projectId}/attachments/${attachmentId}`)
  }

  // Dodajemy metody do komunikacji z nowymi endpointami
  public async createTaskBoardFromProject (projectId: string, data: any): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `task-boards/from-project/${projectId}`, data)
  }

  public async getTaskBoardsByProject (projectId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `task-boards/by-project/${projectId}`)
  }

  // Dodaj metodę do powiązania tablicy zadań z projektem
  public async linkTaskBoardToProject (projectId: string, taskBoardId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.PUT,
      `task-boards/${taskBoardId}/link-project/${projectId}`
    )
  }

  // Dodaj metodę do usuwania powiązania tablicy zadań z projektem
  public async unlinkTaskBoardFromProject (projectId: string, taskBoardId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.DELETE,
      `task-boards/${taskBoardId}/unlink-project/${projectId}`
    )
  }

  // Wysyłanie wiadomości do projektanta
  public async sendMessageToDesigner (designerId: number | string, content: string): Promise<AxiosResponse> {
    try {
      // Najpierw musimy pobrać informacje o projektancie, aby uzyskać ID użytkownika
      const designerResponse = await this.getDesigner(designerId)
      const designer = designerResponse.data

      if (!designer || !designer.userId) {
        throw new Error('Nie można znaleźć użytkownika powiązanego z projektantem')
      }

      // Wykorzystujemy istniejącą metodę sendMessage
      return await this.sendMessage({
        content,
        recipientId: designer.userId
      })
    } catch (error) {
      console.error('Błąd podczas wysyłania wiadomości do projektanta:', error)
      throw error
    }
  }

  // Metoda do pobierania URL dla pliku
  public async getFileUrl (fileKey: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `uploads/presigned-url/${encodeURIComponent(fileKey)}`)
  }

  public async trackProductDownload (productId: number, fileKey: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.POST,
      `brands/products/${productId}/track-download`,
      { fileKey }
    )
  }

  public async createProjectAttachmentFolder (projectId: string, data: {
    name: string
    parentId?: string | null
    isList: boolean
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${projectId}/attachment-folders`, data)
  }

  public async getProjectAttachmentFolders (projectId: string, isList: boolean): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, `projects/${projectId}/attachment-folders?isList=${isList}`)
  }

  public async deleteProjectAttachmentFolder (projectId: string, folderId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `projects/${projectId}/attachment-folders/${folderId}`)
  }

  public async moveProjectAttachment (projectId: string, attachmentId: string, folderId?: string | null): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.PUT,
      `projects/${projectId}/attachments/${attachmentId}/move`,
      { folderId }
    )
  }

  public async activatePremiumTrial (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'user/activate-premium-trial', {})
  }

  public async copyProject (id: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${id}/copy`, {})
  }

  public async getBulkPlantPresignedUrls (plantIds: number[]): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.GET,
      `plants/bulk-presigned-urls?ids=${plantIds.join(',')}`
    )
  }

  public async updateTaskCardNotifyOnComplete (boardId: string, cardId: string, notifyOnComplete: boolean): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.PUT,
      `task-boards/${boardId}/cards/${cardId}/notify-on-complete`,
      { notifyOnComplete }
    )
  }

  public async getProductDownloadStats (brandName?: string): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams()
    if (brandName) {
      queryParams.append('brandName', brandName)
    }
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `brands/products/download-stats${query}`)
  }

  public async getUserProductDownloadStats (brandName?: string): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams()
    if (brandName) {
      queryParams.append('brandName', brandName)
    }
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `brands/products/user-download-stats${query}`)
  }

  public async trackBrandVisit (brandId: number): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.POST, `brands/${brandId}/track-visit`)
  }

  public async getBrandVisitStats (brandName?: string): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams()
    if (brandName) {
      queryParams.append('brandName', brandName)
    }
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `brands/visit-stats${query}`)
  }

  public async getRandomBrands (count?: number): Promise<AxiosResponse> {
    const queryParams = new URLSearchParams()
    if (count) {
      queryParams.append('count', count.toString())
    }
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, `brands/random${query}`)
  }

  // Metody dla etapów harmonogramu
  public async addProjectScheduleStage (projectId: string, data: { name: string, position?: number, startDate?: string, endDate?: string }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, `projects/${projectId}/schedule-stages`, data)
  }

  public async deleteProjectScheduleStage (projectId: string, stageId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.DELETE, `projects/${projectId}/schedule-stages/${stageId}`)
  }

  public async updateProjectScheduleStage (projectId: string, stageId: string, data: { name: string, position?: number, startDate?: string, endDate?: string }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, `projects/${projectId}/schedule-stages/${stageId}`, data)
  }

  public async getProjectScheduleStages (projectId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, `projects/${projectId}/schedule-stages`)
  }

  public async updateProjectItemVisibility (projectId: string, itemId: string, isHidden: boolean): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.PUT,
      `projects/${projectId}/items/${itemId}/visibility`,
      { isHidden }
    )
  }

  async updateProjectItemVisualization (projectId: string, itemId: string, visualizationUrl: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.PUT,
      `projects/${projectId}/items/${itemId}/visualization`,
      { visualizationUrl }
    )
  }

  async updateProjectItemAttachment (projectId: string, itemId: string, attachmentUrl: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.PUT,
      `projects/${projectId}/items/${itemId}/attachment`,
      { attachmentUrl }
    )
  }

  async deleteProjectItemVisualization (projectId: string, itemId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.DELETE,
      `projects/${projectId}/items/${itemId}/visualization`
    )
  }

  async deleteProjectItemAttachment (projectId: string, itemId: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(
      RequestMethod.DELETE,
      `projects/${projectId}/items/${itemId}/attachment`
    )
  }

  // Eksport PDF listy
  public async exportProjectPdf (projectId: string, options: any): Promise<Blob> {
    const response = await fetch(
      `${backendUrl}/projects/${projectId}/export-pdf`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.jwtToken ?? ''}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(options)
      }
    )
    if (!response.ok) throw new Error('Błąd generowania PDF')
    return await response.blob()
  }

  // Eksport moodboard PDF
  public async exportProjectMoodboard (projectId: string, options: any): Promise<Blob> {
    const response = await fetch(
      `${backendUrl}/projects/${projectId}/export-moodboard`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.jwtToken ?? ''}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(options)
      }
    )
    if (!response.ok) throw new Error('Błąd generowania PDF')
    return await response.blob()
  }
}
